import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  Select,
  Table,
  Button,
  Spin,
  Empty,
  Row,
  Col,
  Alert,
  Breadcrumb,
  Space,
  Typography,
} from "antd";
import {
  ReloadOutlined,
  ArrowLeftOutlined,
  HomeOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
} from "../../components/Component";
import ReportsApis from "../../api/WorkOrderManagement/Reports.Apis";
import dropdownApis from "../../api/WorkOrderManagement/DropdownApis";
import { reportConfigs } from "../../data/reportsData";
import * as XLSX from "xlsx";
import usersApis from "../../api/master/users";
import Export from "../../components/export/Export";

const { Option } = Select;
const { Text } = Typography;

interface Site {
  siteID: number;
  siteName: string;
}

interface Contractor {
  contractorID: number;
  companyName: string;
}

interface LedgerRecord {
  date: string;
  particulars: string;
  voucherNo: string;
  debit: string;
  credit: string;
  balance: string;
}

const ContractorLedger: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const flowType = searchParams.get("flowType");
  const [sites, setSites] = useState<Site[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [selectedSite, setSelectedSite] = useState<number | undefined>();
  const [selectedContractor, setSelectedContractor] = useState<
    number | undefined
  >();
  const [ledgerData, setLedgerData] = useState<LedgerRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingContractors, setLoadingContractors] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const reportConfig = reportConfigs["contractor-ledger"];

  const fetchUserSites = async () => {
    try {
      setLoading(true);
      const data = await usersApis.getCurrentUser();
      if (data && data.siteAccessibility) {
        setSites(data.siteAccessibility);
      } else {
        setError("No sites available");
      }
    } catch (error) {
      console.error("Error fetching user sites:", error);
      setError("Failed to load sites");
    } finally {
      setLoading(false);
    }
  };

  const fetchContractorsBySite = async (siteId: number) => {
    try {
      setLoadingContractors(true);
      setError(null);
      const response: any = await dropdownApis.fetchContractorsBySiteIdDropdown(
        {
          id: siteId.toString(),
        }
      );

      if (response?.status && response?.data) {
        // Check if data.status is "Failure" (nested error response)
        if (response.data.status === "Failure") {
          setContractors([]);
          setError(
            response.data.message || "No contractors found for this site"
          );
        } else if (Array.isArray(response.data)) {
          // Data is an array of contractors
          setContractors(response.data);
        } else {
          setContractors([]);
          setError("No contractors found for this site");
        }
      } else {
        setContractors([]);
        setError("No contractors found for this site");
      }
    } catch (error) {
      console.error("Error fetching contractors:", error);
      setError("Failed to load contractors");
      setContractors([]);
    } finally {
      setLoadingContractors(false);
    }
  };

  const fetchLedgerData = async (page: number = 1, pageSize: number = 10) => {
    if (!selectedSite || !selectedContractor) return;

    try {
      setLoading(true);
      setError(null);
      const response: any = await ReportsApis.contractorLedger({
        siteIds: selectedSite,
        contractorIds: selectedContractor,
        page: page,
        pageSize: pageSize,
        flowType: flowType || undefined,
      });

      if (response?.status && response?.data) {
        setLedgerData(response.data.data || []);
        setPagination((prev) => ({
          ...prev,
          current: page,
          pageSize: pageSize,
          total: response.data.totalCount || response.data.data?.length || 0,
        }));
      } else {
        setLedgerData([]);
        setError(response?.message || "No data found");
        setPagination((prev) => ({
          ...prev,
          total: 0,
        }));
      }
    } catch (error: any) {
      console.error("Error fetching ledger data:", error);
      setError(error?.message || "Failed to load ledger data");
      setLedgerData([]);
      setPagination((prev) => ({
        ...prev,
        total: 0,
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination: any) => {
    fetchLedgerData(newPagination.current, newPagination.pageSize);
  };

  const handleExport = () => {
    if (ledgerData.length === 0) {
      return;
    }

    const siteName =
      sites.find((s) => s.siteID === selectedSite)?.siteName || "Unknown Site";
    const contractorName =
      contractors?.find((c) => c.contractorID === selectedContractor)
        ?.companyName || "Unknown Contractor";

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(ledgerData);

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Contractor Ledger");

    // Generate file name
    const fileName = `Contractor_Ledger_${contractorName}_${siteName}_${
      new Date().toISOString().split("T")[0]
    }.xlsx`;

    // Download
    XLSX.writeFile(workbook, fileName);
  };

  const handleBack = () => {
    const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
    navigate(`/work-order-reports${flowTypeQuery}`);
  };

  // useEffect: Fetch user sites on component mount
  useEffect(() => {
    fetchUserSites();
  }, []);

  // useEffect: Fetch contractors when site is selected
  useEffect(() => {
    if (selectedSite) {
      fetchContractorsBySite(selectedSite);
      // Reset contractor selection when site changes
      setSelectedContractor(undefined);
      setLedgerData([]);
      setPagination({
        current: 1,
        pageSize: 10,
        total: 0,
      });
    } else {
      setContractors([]);
      setSelectedContractor(undefined);
      setLedgerData([]);
      setPagination({
        current: 1,
        pageSize: 10,
        total: 0,
      });
    }
  }, [selectedSite]);

  // useEffect: Fetch ledger data when both site and contractor are selected
  useEffect(() => {
    if (selectedSite && selectedContractor) {
      setPagination({
        current: 1,
        pageSize: 10,
        total: 0,
      });
      fetchLedgerData(1, 10);
    }
  }, [selectedSite, selectedContractor]);

  return (
    <>
      <Head title="Contractor Ledger" />
      <Content>
        <Card>
          <BlockHead size="lg">
            <Breadcrumb
              items={[
                {
                  href: "/",
                  title: <HomeOutlined />,
                },
                {
                  href: flowType
                    ? `/work-order-reports?flowType=${flowType}`
                    : "/work-order-reports",
                  title: "Work Order Reports",
                },
                {
                  title: "Contractor Ledger",
                },
              ]}
              style={{ marginBottom: 16 }}
            />
            <BlockBetween>
              <BlockHeadContent>
                <BlockTitle tag="h4">{reportConfig?.title}</BlockTitle>
                <Text type="secondary">
                  Track all financial transactions for contractors including RA
                  Bills, payments, debits and credits
                </Text>
              </BlockHeadContent>
              <BlockHeadContent>
                <Space>
                  <Export
                    data={{
                      sortOrder: "DESC",
                      pageSize: 100,
                      direction: "P",
                      pageDiff: 0,
                      searchQuery: null,
                      siteId: selectedSite?.toString(),
                      contractorId: selectedContractor?.toString(),
                      supplierId: undefined,
                      materialId: undefined,
                      categoryId: undefined,
                      flowType: flowType || undefined,
                    }}
                    moduleKey={"contractor-ledger"}
                    showAsButton={true}
                    disabled={!selectedSite || !selectedContractor}
                  />

                  <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
                    Back
                  </Button>
                </Space>
              </BlockHeadContent>
            </BlockBetween>
          </BlockHead>

          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <label className="form-label">Site</label>
              <Select
                showSearch
                placeholder="Select Site"
                style={{ width: "100%" }}
                value={selectedSite}
                onChange={(value) => setSelectedSite(value)}
                filterOption={(input, option: any) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {sites.map((site) => (
                  <Option key={site.siteID} value={site.siteID}>
                    {site.siteName}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <label className="form-label">Contractor</label>
              <Select
                showSearch
                placeholder="Select Contractor"
                style={{ width: "100%" }}
                value={selectedContractor}
                onChange={(value) => setSelectedContractor(value)}
                disabled={!selectedSite || loadingContractors}
                loading={loadingContractors}
                filterOption={(input, option: any) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {contractors?.map((contractor) => (
                  <Option
                    key={contractor.contractorID}
                    value={contractor.contractorID}
                  >
                    {contractor.companyName}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <label className="form-label" style={{ visibility: "hidden" }}>
                Action
              </label>
              <div>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setPagination({
                      current: 1,
                      pageSize: 10,
                      total: 0,
                    });
                    fetchLedgerData(1, 10);
                  }}
                  disabled={!selectedSite || !selectedContractor || loading}
                >
                  Refresh
                </Button>
              </div>
            </Col>
          </Row>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              style={{ marginBottom: 16 }}
              onClose={() => setError(null)}
            />
          )}

          {!selectedSite || !selectedContractor ? (
            <Empty
              description="Please select both Site and Contractor to view ledger data"
              style={{ padding: "50px 0" }}
            />
          ) : loading ? (
            <div style={{ textAlign: "center", padding: "50px 0" }}>
              <Spin size="large" />
              <p style={{ marginTop: 16 }}>Loading ledger data...</p>
            </div>
          ) : (
            <Table
              columns={reportConfig?.columns}
              dataSource={ledgerData}
              rowKey={(record, index) => `ledger-${index}`}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} entries`,
                pageSizeOptions: ["10", "20", "30", "40", "50"],
              }}
              onChange={handleTableChange}
              scroll={{ x: "max-content" }}
              bordered
            />
          )}
        </Card>
      </Content>
    </>
  );
};

export default ContractorLedger;
